import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { 
  IndianRupee, Calendar, Store, RefreshCw, Search, 
  TrendingUp, Clock, Building2, ChevronLeft, ChevronRight
} from 'lucide-react';
import { storeExpensesAPI, storesAPI } from '@/services/api';

const STORE_EXPENSE_CATEGORIES = [
  { id: 'utilities', name: 'Utilities' },
  { id: 'rent', name: 'Rent' },
  { id: 'maintenance', name: 'Maintenance' },
  { id: 'supplies', name: 'Store Supplies' },
  { id: 'salaries', name: 'Salaries & Wages' },
  { id: 'transport', name: 'Transport' },
  { id: 'marketing', name: 'Marketing' },
  { id: 'miscellaneous', name: 'Miscellaneous' }
];

export default function StoreExpensesMonitor() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('list');
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [stores, setStores] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    store_id: '',
    category: '',
    from_date: '',
    to_date: ''
  });

  useEffect(() => {
    loadStores();
    loadSummary();
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [filters, pagination.page]);

  const loadStores = async () => {
    try {
      const data = await storesAPI.getDropdown();
      setStores(data || []);
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        page_size: 50
      };
      if (filters.store_id) params.store_id = filters.store_id;
      if (filters.category) params.category = filters.category;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;

      const response = await storeExpensesAPI.hqListAll(params);
      setExpenses(response.data?.items || []);
      setPagination({
        page: response.data?.page || 1,
        total: response.data?.total || 0,
        total_pages: response.data?.total_pages || 0
      });
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast({ title: 'Error', description: 'Failed to load store expenses', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const params = {};
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      
      const response = await storeExpensesAPI.hqGetSummary(params);
      setSummary(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR', 
      maximumFractionDigits: 0 
    }).format(amount || 0);
  };

  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const formattedDate = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    
    // Extract time from created_at if available
    if (timeStr) {
      const time = new Date(timeStr);
      const formattedTime = time.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
      return { date: formattedDate, time: formattedTime };
    }
    return { date: formattedDate, time: null };
  };

  const getCategoryName = (categoryId) => {
    const cat = STORE_EXPENSE_CATEGORIES.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  const handleRefresh = () => {
    loadExpenses();
    loadSummary();
  };

  const clearFilters = () => {
    setFilters({ store_id: '', category: '', from_date: '', to_date: '' });
    setPagination(p => ({ ...p, page: 1 }));
  };

  return (
    <div className="p-6 space-y-6" data-testid="store-expenses-monitor-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-100 rounded-xl">
            <Store className="w-8 h-8 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Store Expenses Monitor</h1>
            <p className="text-gray-500">View expenses from all stores</p>
          </div>
        </div>
        <Button onClick={handleRefresh} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Total Store Expenses</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.total_amount)}</p>
                  <p className="text-sm text-emerald-100">{summary.total_count} entries</p>
                </div>
                <IndianRupee className="w-8 h-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Stores with Expenses</p>
                  <p className="text-2xl font-bold">{summary.store_count || 0}</p>
                  <p className="text-sm text-blue-100">Active stores</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Categories Used</p>
                  <p className="text-2xl font-bold">{summary.by_category?.length || 0}</p>
                  <p className="text-sm text-purple-100">Expense types</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Top Category</p>
                  <p className="text-xl font-bold truncate">
                    {summary.by_category?.[0]?._id ? getCategoryName(summary.by_category[0]._id) : '-'}
                  </p>
                  <p className="text-sm text-amber-100">
                    {summary.by_category?.[0] ? formatCurrency(summary.by_category[0].total_amount) : '-'}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list" className="gap-2">
            <Store className="w-4 h-4" /> All Expenses
          </TabsTrigger>
          <TabsTrigger value="by-store" className="gap-2">
            <Building2 className="w-4 h-4" /> By Store
          </TabsTrigger>
          <TabsTrigger value="by-category" className="gap-2">
            <TrendingUp className="w-4 h-4" /> By Category
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <Card className="mt-4">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="min-w-[180px]">
                <Label className="text-xs">Store</Label>
                <Select value={filters.store_id || 'all'} onValueChange={(v) => setFilters({ ...filters, store_id: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9" data-testid="filter-store">
                    <SelectValue placeholder="All Stores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stores</SelectItem>
                    {stores.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-[150px]">
                <Label className="text-xs">Category</Label>
                <Select value={filters.category || 'all'} onValueChange={(v) => setFilters({ ...filters, category: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9" data-testid="filter-category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {STORE_EXPENSE_CATEGORIES.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">From Date</Label>
                <Input
                  type="date"
                  className="h-9"
                  value={filters.from_date}
                  onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                  data-testid="filter-from-date"
                />
              </div>

              <div>
                <Label className="text-xs">To Date</Label>
                <Input
                  type="date"
                  className="h-9"
                  value={filters.to_date}
                  onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                  data-testid="filter-to-date"
                />
              </div>

              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Expenses List Tab */}
        <TabsContent value="list" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading expenses...</div>
              ) : expenses.length === 0 ? (
                <div className="text-center py-12">
                  <Store className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No store expenses found</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Date & Time</TableHead>
                        <TableHead>Store</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Payment Mode</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map((expense) => {
                        const { date, time } = formatDateTime(expense.expense_date, expense.created_at);
                        return (
                          <TableRow key={expense.id} className="hover:bg-gray-50" data-testid={`expense-row-${expense.id}`}>
                            <TableCell>
                              <div>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <span className="font-medium">{date}</span>
                                </div>
                                {time && (
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                                    <Clock className="w-3 h-3" />
                                    {time}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Store className="w-4 h-4 text-emerald-500" />
                                <span className="font-medium">{expense.store_name || '-'}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{getCategoryName(expense.category)}</Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-gray-700 max-w-[200px] truncate block">{expense.description}</span>
                              {expense.terminal_name && (
                                <span className="text-xs text-gray-400">Terminal: {expense.terminal_name}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs capitalize">
                                {expense.payment_mode?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-semibold text-red-600">{formatCurrency(expense.amount)}</span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {pagination.total_pages > 1 && (
                    <div className="flex items-center justify-between p-4 border-t">
                      <span className="text-sm text-gray-500">
                        Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page === 1}
                          onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                        >
                          <ChevronLeft className="w-4 h-4" /> Prev
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pagination.page === pagination.total_pages}
                          onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                        >
                          Next <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* By Store Tab */}
        <TabsContent value="by-store" className="mt-4">
          <div className="grid gap-3">
            {summary?.by_store?.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No store expenses found
                </CardContent>
              </Card>
            ) : (
              summary?.by_store?.map((store) => (
                <Card key={store._id} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Store className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{store.store_name || store._id}</p>
                          <p className="text-sm text-gray-500">{store.count} expense entries</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-red-600">{formatCurrency(store.total_amount)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* By Category Tab */}
        <TabsContent value="by-category" className="mt-4">
          <div className="grid gap-3">
            {summary?.by_category?.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No expenses found
                </CardContent>
              </Card>
            ) : (
              summary?.by_category?.map((cat) => (
                <Card key={cat._id} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="px-3 py-1">{cat._id}</Badge>
                        <div>
                          <p className="font-semibold text-gray-800">{getCategoryName(cat._id)}</p>
                          <p className="text-sm text-gray-500">{cat.count} entries</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-red-600">{formatCurrency(cat.total_amount)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
